'use client';
import React from 'react';
import { Permission } from '@benzinga/session';
import { SessionContext } from '@benzinga/session-context';
import { WidgetContext } from '@benzinga/widget-tools';
import { useIsUserLoggedIn } from './hooks/useIsUserLoggedIn';
import { usePermissions } from './hooks/usePermissions';
import { UpSellPrompt } from './upSellPrompt';
import { BlockFreenium } from './blockFreenium';
import { LoginPrompt } from './loginPrompt';
import { Slug } from './entity';
import { TrackingManager } from '@benzinga/tracking-manager';

interface Props {
  resourceName?: string;
  isFeatureUpdate?: boolean;
  onClose?: (upgraded: boolean) => void;
  permission?: Permission[] | Permission;
  permissionType?: 'and' | 'or';
  showUpsell?: boolean;
  disableTrial?: boolean;
  slug?: Slug;
  trackingInfoAppName?: string;
  trackingInfoMessage?: string;
}

export const PermissionPrompt: React.FC<Props> = props => {
  const session = React.useContext(SessionContext);
  const widgetContext = React.useContext(WidgetContext);

  const loggedIn = useIsUserLoggedIn();
  const permissions = React.useMemo(
    () => (Array.isArray(props.permission) ? props.permission : props.permission ? [props.permission] : []),
    [props.permission],
  );
  const hasAccess = usePermissions(permissions, props.permissionType ?? 'and');
  const [isDismissed, setIsDismissed] = React.useState(false);

  // blocking access for free users, showing upsell for basic users and above temprory block on the frontend while we work on the backend solution
  const chatPermission = usePermissions([{ action: 'chatroom/list', resource: '#' }]);
  const hasSensaAccess = usePermissions([{ action: 'bzpro/widget/upleg', resource: '#' }]);
  const firstRender = React.useRef(true);
  React.useEffect(() => {
    if (firstRender.current && loggedIn) {
      session.getManager(TrackingManager).trackCampaignEvent('view', {
        partner_id: `internal_upsell - ${props.trackingInfoMessage}`,
        unit_type: widgetContext.widgetType ?? props.trackingInfoAppName,
      });
    }
    firstRender.current = false;
  }, [loggedIn, props.trackingInfoAppName, props.trackingInfoMessage, session, widgetContext.widgetType]);

  const componentName = React.useMemo(() => {
    const baseName = widgetContext?.widgetType;
    const resourceName =
      typeof props.permission === 'object' && !Array.isArray(props.permission)
        ? props.permission?.resource !== '#'
          ? props.permission?.resource
          : ''
        : '';
    if (props.resourceName) {
      return props.resourceName;
    }
    if (baseName && resourceName) {
      return `${baseName}-${resourceName}`;
    }
    if (!baseName && !resourceName) {
      return 'squawk';
    }
    return `${baseName}${resourceName}`.trim();
  }, [widgetContext.widgetType, props.permission, props.resourceName]);

  const onToggleUpSellModal = React.useCallback(
    (upgraded: boolean) => {
      if (loggedIn) {
        session.getManager(TrackingManager).trackCampaignEvent('view', {
          partner_id: `internal_upsell_closed${upgraded ? '_upgraded' : ''} - ${props.trackingInfoMessage}`,
          unit_type: widgetContext.widgetType ?? props.trackingInfoAppName,
        });
      }
      setIsDismissed(true);
      const onClose = props.onClose;
      onClose?.(upgraded);
    },
    [loggedIn, props.onClose, props.trackingInfoAppName, props.trackingInfoMessage, session, widgetContext.widgetType],
  );

  const shouldShowUpsell = props.showUpsell || (props.permission !== undefined && !hasAccess);

  if (!shouldShowUpsell) {
    return null;
  }

  if (isDismissed) {
    return null;
  }
  if (!loggedIn) {
    return <LoginPrompt onClose={onToggleUpSellModal} />;
  }
  if (chatPermission || hasSensaAccess) {
    return (
      <UpSellPrompt
        componentName={componentName}
        disableTrial={props.disableTrial}
        onClose={onToggleUpSellModal}
        slug={props?.slug}
      />
    );
  }
  return <BlockFreenium componentName={componentName} disableTrial={props.disableTrial} slug={props?.slug} />;
};
